/**
 * Which way a query is translated.
 *
 * The client detects the direction once and sends it explicitly on every v2
 * translate request; the worker validates the value but never re-detects it.
 * Both the SPA and the translate worker import this file rather than keeping a
 * copy each, so the detection heuristic cannot drift between them.
 *
 * Dependency-free on purpose: it is bundled into a Cloudflare Worker as well as
 * the browser build.
 */

export type TranslationDirection = 'thai' | 'english'

/**
 * The direction on the wire and in the cache: v2 request field, and the scope
 * of every translation_cache row. Rows must be scoped by this: the two stored
 * fields mean different things each way round (English→Thai stores the Thai
 * translation as spacedText and the original English as translation), so
 * serving a row to the other direction returns the input text where a
 * translation belongs.
 */
export type DirectionKey = 'th-en' | 'en-th'

export const isDirectionKey = (value: unknown): value is DirectionKey =>
   value === 'th-en' || value === 'en-th'

/**
 * The dominant language of a query, which decides the direction it is
 * translated in. Ties go to Thai — the site's primary job.
 */
export const detectDominantLanguage = (text: string): TranslationDirection => {
   const thaiCount = (text.match(/[\u0E00-\u0E7F]/g) || []).length
   const engCount = (text.match(/[a-zA-Z]/g) || []).length
   return thaiCount >= engCount ? 'thai' : 'english'
}

/**
 * Legacy positional stride: Thai→English is [spaced Thai][English],
 * English→Thai is [natural Thai][spaced Thai][original English]. Only the
 * worker's legacy response path still assembles this shape, for SPA bundles
 * that predate the v2 contract; delete alongside that path.
 */
export const linesPerEntryFor = (direction: TranslationDirection): 2 | 3 =>
   direction === 'thai' ? 2 : 3

export const directionKeyFor = (direction: TranslationDirection): DirectionKey =>
   direction === 'thai' ? 'th-en' : 'en-th'
